import { maskAccountNumber } from "../dbbl/utils/masks/accountDestination.mask";

import type { FluentValue, FluentFunction } from "@fluent/bundle";

/**
 * Extracts the raw string value from a FluentValue.
 * FluentValue can be either a string or a FluentType instance.
 */
const getStringValue = (value: FluentValue): string => {
  if (typeof value === "string") return value;
  return String(value.valueOf());
};

/**
 * Custom Fluent function that masks an account number by showing
 * the last 4 characters. This mirrors the existing `getMaskedNumber()`
 * logic from Account.ts.
 *
 * Usage in FTL:
 *   account-description = { $accountName } - { ACCOUNT_MASK_NUMBER($accountNumber) }
 */

const ACCOUNT_MASK_NUMBER: FluentFunction = (
  positional: FluentValue[],
): FluentValue => {
  return maskAccountNumber(getStringValue(positional[0] || ""));
};

/**
 * Custom Fluent function that masks an account number by showing
 * a prefix of the first 3 characters from the beginning of the account number.
 * This helps distinguish accounts that share similar trailing digits
 * (e.g. IBS loan accounts).
 *
 * Usage in FTL:
 *   account-description = { $accountName } - { ACCOUNT_MASK_NUMBER_WITH_PREFIX($accountNumber) }
 *   account-description = { $accountName } - { ACCOUNT_MASK_NUMBER_WITH_PREFIX($accountNumber, prefixLength: 5) }
 */
const ACCOUNT_MASK_NUMBER_WITH_PREFIX: FluentFunction = (
  positional: FluentValue[],
  named: Record<string, FluentValue>,
): FluentValue => {
  let prefixLength: number = 3; // Default prefix length
  // Protects us from faulty prefixLength parameters.
  try {
    if (named.prefixLength) {
      const parsedValue = (named.prefixLength as { value: number }).value;
      if (typeof parsedValue === "number" && parsedValue >= 0) {
        prefixLength = parsedValue;
      }
    }
  } catch (error) {
    console.warn(
      "Invalid prefixLength argument in ACCOUNT_MASK_NUMBER_WITH_PREFIX:",
      error,
    );
  }

  const accountNumber = getStringValue(positional[0] || "");
  const maskedNumber = maskAccountNumber(accountNumber);

  // Some account numbers may be shorter than the combined length of the prefix and masked number,
  const adjustedPrefixLength = Math.min(
    prefixLength,
    accountNumber.length - maskedNumber.length,
  );

  if (adjustedPrefixLength > 0) {
    return `${accountNumber.slice(0, adjustedPrefixLength)}***${maskedNumber}`;
  }
  return maskedNumber;
};

/**
 * Custom Fluent function that masks an account number by showing
 * only the last X characters (default 8). The suffix length is
 * configurable via the `suffixLength` named parameter.
 *
 * Usage in FTL:
 *   account-description = { ACCOUNT_MASK_NUMBER_SUFFIX($accountNumber) }
 *   account-description = { ACCOUNT_MASK_NUMBER_SUFFIX($accountNumber, suffixLength: 6) }
 */
const ACCOUNT_MASK_NUMBER_SUFFIX: FluentFunction = (
  positional: FluentValue[],
  named: Record<string, FluentValue>,
): FluentValue => {
  let suffixLength: number = 8; // Default suffix length
  // Protects us from faulty suffixLength parameters.
  try {
    if (named.suffixLength) {
      const parsedValue = (named.suffixLength as { value: number }).value;
      if (typeof parsedValue === "number" && parsedValue >= 0) {
        suffixLength = parsedValue;
      }
    }
  } catch (error) {
    console.warn(
      "Invalid suffixLength argument in ACCOUNT_MASK_NUMBER_SUFFIX:",
      error,
    );
  }

  const accountNumber = getStringValue(positional[0] || "");
  return maskAccountNumber(accountNumber, suffixLength);
};

/**
 * All custom Fluent functions to be registered with FluentBundle instances.
 * Function names in FTL are case-insensitive, but by convention we use
 * SCREAMING_SNAKE_CASE to distinguish them from variables.
 */
export const customFluentFunctions: Record<string, FluentFunction> = {
  ACCOUNT_MASK_NUMBER,
  ACCOUNT_MASK_NUMBER_WITH_PREFIX,
  ACCOUNT_MASK_NUMBER_SUFFIX,
};
